// ProcessorGob — the galaxite refinery: BuildComplete spawns a free docked miner, the
// docking choreography (GetMiner -> TakeGalaxite -> PutMiner) converts 2 galaxite into
// 2*knGalaxiteValue credits per update (capacity-gated -> the storage-full trigger
// condition), door-tile kbfStructure twiddles keep the dock approach clear, and dying
// frees/destroys the docked miner. + PuffGob (the processing smoke).
// game/Processor.cpp (all 635 lines).

import { GetSideMask } from '../types.ts';
import { markDeadSpan } from '../sm/stream.ts';
import type { SimContext } from '../context.ts';
import { kfGobActive } from './gob.ts';
import { StructGob } from './struct.ts';
import {
  StructConsts,
  InitStructConstsClass,
  kfUntcHasFullnessIndicator,
  kwcTile,
} from './unitconsts.ts';
import { kgtProcessor } from './gobtypes.ts';
import { kutProcessor, kfHcapIncreasedMinerLoadValue } from '../player/side.ts';
import { kumReactor } from '../player/masks.ts';
import type { Player } from '../player/player.ts';
import { MinerGob, knGalaxiteValue } from './miner.ts';
import { knIncreasedMinerLoadValuePercent } from '../player/masks.ts';
import { AnimGob, kfAnmDeleteWhenDone, kfAnmSmokeFireLayer } from './animgob.ts';
import { knLimitMobileUnit, knLimitSupport } from './gobmgr.ts';
import {
  Message,
  knHandled,
  kstReservedGlobal,
  kmidReservedEnter,
  kmidReservedExit,
  kmidReservedUpdate,
  kmidGalaxiteDelivery,
  kmidAnimationComplete,
  kmidBuildComplete,
  kmidMineCommand,
  kgidNull,
  kstBeingBuilt,
  kstIdle,
  kstDying,
  kstProcessorGetMiner,
  kstProcessorTakeGalaxite,
  kstProcessorPutMiner,
  newMessage,
} from '../sm/message.ts';
import { kbfStructure, kbfMobileUnit } from '../terrain/terrainmap.ts';
import { TcFromWc, WcFromTile16ths } from '../math/coords.ts';
import { kwcTile16th } from './unitconsts.ts';
import { kwxInvalid } from './mobileunit.ts';
import { Animation } from '../anim/animation.ts';
import { idiv } from '../math/int32.ts';
import { registerGobType } from './factory.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxNothing,
  ksfxGalaxiteProcessorAbortRepair,
  ksfxGalaxiteProcessorRepair,
  ksfxGalaxiteProcessorDamaged,
  ksfxGalaxiteProcessorSelect,
  ksfxGalaxiteProcessorDestroyed,
  ksfxGalaxiteProcessorDoorOpening,
  ksfxGalaxiteProcessorDoorClosing,
} from '../data/soundeffects.ts';
import type { TPoint, WPoint } from '../types.ts';

// game/Processor.cpp:16-33
export function InitProcessorClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new StructConsts().bind(ctx);
  c.gt = kgtProcessor;
  c.ut = kutProcessor;
  c.umPrerequisites = kumReactor;
  c.wf |= kfUntcHasFullnessIndicator;
  // Sound effects (Processor.cpp:25-30)
  c.sfxAbortRepair = ksfxGalaxiteProcessorAbortRepair;
  c.sfxRepair = ksfxGalaxiteProcessorRepair;
  c.sfxDamaged = ksfxGalaxiteProcessorDamaged;
  c.sfxSelect = ksfxGalaxiteProcessorSelect;
  c.sfxDestroyed = ksfxGalaxiteProcessorDestroyed;
  c.sfxImpact = ksfxNothing;
  return InitStructConstsClass(ctx, c, pini);
}

export class ProcessorGob extends StructGob {
  m_aniOverlay = new Animation();
  m_gidMiner = kgidNull;
  m_wptFakeMiner: WPoint = { wx: kwxInvalid, wy: 0 };
  m_fProcessingAnimationInProgress = false;
  m_fDoorMoving = false;

  // game/Processor.cpp:40-47
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutProcessor] as StructConsts);
    this.m_aniOverlay.Init(ctx.animDataFor(this.m_pstruc.szAniName));
    this.StartAnimation(this.m_aniOverlay, 3, 0, 0);
  }

  // game/Processor.cpp:101-116 — takeover needs unit-limit space for the docked miner
  override IsTakeoverable(pplr: Player): boolean {
    if (this.m_gidMiner !== kgidNull) {
      const pgobMiner = this.ctx.gobm.GetGob(this.m_gidMiner, false) as MinerGob | null;
      if (pgobMiner !== null) {
        if (!this.ctx.gobm.IsBelowLimit(knLimitMobileUnit, pplr)) {
          // (local-player alert: presentation)
          return false;
        }
      }
    }
    return super.IsTakeoverable(pplr);
  }

  // game/Processor.cpp:120-150 — credits share + the docked miner change hands
  override Takeover(pplr: Player): void {
    const cCreditsTaken = CalcCreditsShareLocal(this.m_pplr as Player);
    (this.m_pplr as Player).SetCredits((this.m_pplr as Player).GetCredits() - cCreditsTaken, true);
    pplr.SetCredits(pplr.GetCredits() + cCreditsTaken, true);

    if (this.m_gidMiner !== kgidNull) {
      const pgobMiner = this.ctx.gobm.GetGob(this.m_gidMiner, false) as MinerGob | null;
      if (pgobMiner !== null) {
        pgobMiner.Deactivate();
        this.ctx.gobm.TrackGobCounts(pgobMiner, false);
        pgobMiner.SetOwner(pplr);
        this.ctx.gobm.TrackGobCounts(pgobMiner, true);
        pgobMiner.Activate();
      }
    }

    super.Takeover(pplr);
  }

  // game/Processor.cpp:254-275
  NotifyMinersAttemptingDelivery(fDying: boolean): void {
    for (
      let pgobT = this.ctx.gobm.GetFirstGob();
      pgobT !== null;
      pgobT = this.ctx.gobm.GetNextGob(pgobT)
    ) {
      if (pgobT.GetType() !== kutGalaxMinerGt) continue;
      if (pgobT.GetOwner() !== this.m_pplr) continue;
      if (!(pgobT.GetFlags() & kfGobActive)) continue;
      const pmnr = pgobT as MinerGob;
      if (pmnr.IsAttemptingToDeliver(this.m_gid)) {
        pmnr.SendDeliverCommand(fDying ? kgidNull : this.m_gid);
      }
    }
  }

  // game/Processor.cpp:288-581
  // game/Processor.cpp:51-78 (knVerProcessorGobState 2)
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 2) return false;
    this.m_gidMiner = pstm.ReadWord();
    this.m_wptFakeMiner.wx = (pstm.ReadWord() << 16) >> 16;
    this.m_wptFakeMiner.wy = (pstm.ReadWord() << 16) >> 16;
    this.m_fProcessingAnimationInProgress = pstm.ReadByte() !== 0;
    this.m_fDoorMoving = pstm.ReadByte() !== 0;
    this.m_aniOverlay.LoadState(pstm); // Init'd by the ctor with the processor anim
    return super.LoadState(pstm);
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(2);
    pstm.WriteWord(this.m_gidMiner);
    pstm.WriteWord(this.m_wptFakeMiner.wx);
    pstm.WriteWord(this.m_wptFakeMiner.wy);
    markDeadSpan(pstm, 2); // the ctor inits only .wx (Processor.cpp:45) (B20)
    pstm.WriteByte(this.m_fProcessingAnimationInProgress ? 1 : 0);
    pstm.WriteByte(this.m_fDoorMoving ? 1 : 0);
    markDeadSpan(pstm, 1); // C++ ctor never inits it — garbage until the door first moves (B20)
    this.m_aniOverlay.SaveState(pstm);
    return super.SaveState(pstm);
  }

  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    const trmap = this.ctx.trmap!;

    if (st === kstReservedGlobal) {
      switch (pmsg.mid) {
        case kmidGalaxiteDelivery: {
          // Remember the miner and hide it (it still occupies terrain + unit counts)
          this.m_gidMiner = pmsg.smidSender;
          const pgobMiner = this.ctx.gobm.GetGob(this.m_gidMiner, false) as MinerGob;
          pgobMiner.Hide(true);

          const tpt: TPoint = { tx: 0, ty: 0 };
          pgobMiner.GetTilePosition(tpt);
          trmap.SetFlags(tpt.tx, tpt.ty, 1, 1, kbfStructure);
          trmap.ClearFlags(tpt.tx, tpt.ty, 1, 1, kbfMobileUnit);

          this.SetState(kstProcessorGetMiner);
          return knHandled;
        }

        case kmidAnimationComplete:
          this.m_fProcessingAnimationInProgress = false;
          this.m_unvl.MinSkip();
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    //-----------------------------------------------------------------------
    if (st === kstBeingBuilt) {
      switch (pmsg.mid) {
        case kmidBuildComplete: {
          // A run-time-built Processor brings a fresh miner with itself (free!)
          if (this.ctx.gobm.IsBelowLimit(knLimitMobileUnit, this.m_pplr as Player)) {
            const pmnr = new MinerGob(this.ctx);
            if (
              pmnr.Init(this.m_wx + kwcTile, this.m_wy + kwcTile * 2, this.m_pplr as Player, 0, 0)
            ) {
              pmnr.SetFavoriteProcessor(this.m_gid);

              // Hide so we can 'launch' it like a post-delivery exit
              this.m_gidMiner = pmnr.GetId();
              pmnr.GetCenter(this.m_wptFakeMiner);
              this.m_wptFakeMiner.wy = this.m_wy + WcFromTile16ths(15);
              pmnr.Hide(true);

              const tpt: TPoint = { tx: 0, ty: 0 };
              pmnr.GetTilePosition(tpt);
              trmap.SetFlags(tpt.tx, tpt.ty, 1, 1, kbfStructure);
              trmap.ClearFlags(tpt.tx, tpt.ty, 1, 1, kbfMobileUnit);

              this.StartAnimation(this.m_aniOverlay, 3, -1, 0);
              this.SetState(kstProcessorPutMiner);
            } else {
              this.clearDoorSpot(trmap);
              this.SetState(kstIdle);
            }
          } else {
            // No miner created: clear the reserved spot so others can dock
            this.clearDoorSpot(trmap);
            this.SetState(kstIdle);
          }
          this.Activate();
          return knHandled;
        }

        case kmidReservedEnter:
          // Mark the spot in front of the front door occupied so the miner can exit
          // after building completes (twiddled on delivery and PutMiner)
          trmap.SetFlags(
            TcFromWc(this.m_wx) + 1,
            TcFromWc(this.m_wy) + this.m_pstruc.ctyTiles,
            1,
            1,
            kbfStructure,
          );
          return super.ProcessStateMachineMessage(st, pmsg);
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    //-----------------------------------------------------------------------
    if (st === kstProcessorGetMiner) {
      switch (pmsg.mid) {
        case kmidReservedEnter: {
          const pgobMiner = this.ctx.gobm.GetGob(this.m_gidMiner, false) as MinerGob | null;
          if (pgobMiner !== null) {
            pgobMiner.GetCenter(this.m_wptFakeMiner);
            pgobMiner.Select(false);
            // (Hilight(false): presentation)
            this.StartAnimation(this.m_aniOverlay, 3, 0, 0);
            this.m_fDoorMoving = false;
          }
          return knHandled;
        }
        case kmidReservedExit:
          return knHandled; // keep StructGob from handling an unknown state

        case kmidReservedUpdate: {
          this.m_unvl.MinSkip();

          // Open the door when the miner gets close
          if (this.m_wptFakeMiner.wy < this.m_wy + WcFromTile16ths(35)) {
            this.AdvanceAnimation(this.m_aniOverlay);
            if (!this.m_fDoorMoving) {
              this.m_fDoorMoving = true;
              if (this.m_pplr === this.ctx.plrLocal)
                this.ctx.host.PlaySfx(ksfxGalaxiteProcessorDoorOpening); // Processor.cpp:402
            }
          }

          if (this.m_wptFakeMiner.wy > this.m_wy + WcFromTile16ths(15)) {
            this.m_wptFakeMiner.wy -= kwcTile16th;
            this.MarkRedraw();
          } else {
            this.SetState(kstProcessorTakeGalaxite);
          }

          this.DefUpdate();
          return knHandled;
        }
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstProcessorTakeGalaxite) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
        case kmidReservedExit:
          return knHandled;

        case kmidReservedUpdate: {
          this.m_unvl.MinSkip();

          const pgobMiner = this.ctx.gobm.GetGob(this.m_gidMiner, false) as MinerGob;
          let nAmount = pgobMiner.GetGalaxiteAmount();
          if (nAmount > 0) {
            nAmount -= 2;
            const nGalaxiteValue =
              this.m_pplr.GetHandicap() & kfHcapIncreasedMinerLoadValue
                ? idiv(knGalaxiteValue * (100 + knIncreasedMinerLoadValuePercent) + 50, 100)
                : knGalaxiteValue;
            const nCreditsNew = (this.m_pplr as Player).GetCredits() + 2 * nGalaxiteValue;
            if (nCreditsNew <= (this.m_pplr as Player).GetCapacity()) {
              this.MarkRedraw();
              (this.m_pplr as Player).SetCredits(nCreditsNew, true);
              pgobMiner.SetGalaxiteAmount(nAmount);

              // Kick off the processing smoke if not already in progress
              if (!this.m_fProcessingAnimationInProgress) {
                if (this.ctx.gobm.IsBelowLimit(knLimitSupport)) {
                  const pgob = new PuffGob(this.ctx);
                  pgob.InitPuff(this.m_wx, this.m_wy, this.m_gid);
                }
                this.m_fProcessingAnimationInProgress = true;
              }
            } else {
              // Need more storage! (the trigger condition is SIM once triggers port)
              // Processor.cpp:452 — direct, like the C++ (a deferred seam here once
              // silently dropped these; see sim-quirks 'Port bugs CAUGHT' #9)
              this.ctx
                .sim!.GetLevel()
                ?.GetTriggerMgr?.()
                ?.SetConditionTrue(
                  3 /* knGalaxiteCapacityReachedCondition */,
                  GetSideMask(this.GetSide()),
                );
              // Processor.cpp:459-466 — the local player's too-full raise; the
              // 30s wall-clock throttle + alert + ksfxGalaxiteWarehouseTooFull
              // live on the host (gamesession.ts NotifyStorageFull)
              if (this.m_pplr === this.ctx.plrLocal) this.ctx.host.NotifyStorageFull();
            }
          } else {
            this.SetState(kstProcessorPutMiner);
          }
          this.DefUpdate();
          return knHandled;
        }
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstProcessorPutMiner) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.m_fDoorMoving = false;
          return knHandled;

        case kmidReservedExit: {
          // Restore the miner
          const pgobMiner = this.ctx.gobm.GetGob(this.m_gidMiner, false) as MinerGob;
          pgobMiner.Hide(false);

          const tpt: TPoint = { tx: 0, ty: 0 };
          pgobMiner.GetTilePosition(tpt);
          trmap.ClearFlags(tpt.tx, tpt.ty, 1, 1, kbfStructure);
          trmap.SetFlags(tpt.tx, tpt.ty, 1, 1, kbfMobileUnit);

          // Resume mining via a MESSAGE so the state change is immediate
          const msg = newMessage(kmidMineCommand, kgidNull, pgobMiner.GetId());
          msg.MineCommand.gidTarget = kgidNull;
          msg.MineCommand.wptTarget.wx = kwxInvalid;
          msg.MineCommand.wptTarget.wy = 0;
          this.ctx.smm!.SendMsg(msg);

          this.m_gidMiner = kgidNull;
          this.m_wptFakeMiner.wx = kwxInvalid;

          this.NotifyMinersAttemptingDelivery(false);
          return knHandled;
        }

        case kmidReservedUpdate: {
          this.m_unvl.MinSkip();

          const wptDst: WPoint = { wx: 0, wy: 0 };
          (this.ctx.gobm.GetGob(this.m_gidMiner, false) as MinerGob).GetCenter(wptDst);
          if (this.m_wptFakeMiner.wy > this.m_wy + WcFromTile16ths(27)) {
            const ifrm = this.m_aniOverlay.GetFrame();
            if (ifrm > 0) {
              this.SetAnimationFrame(this.m_aniOverlay, ifrm - 1);
              if (!this.m_fDoorMoving) {
                this.m_fDoorMoving = true;
                if (this.m_pplr === this.ctx.plrLocal)
                  this.ctx.host.PlaySfx(ksfxGalaxiteProcessorDoorClosing); // Processor.cpp:515
              }
            }
          }

          if (this.m_wptFakeMiner.wy < wptDst.wy) {
            this.m_wptFakeMiner.wy += kwcTile16th;
            this.MarkRedraw();
          } else {
            this.SetState(kstIdle);
          }
          this.DefUpdate();
          return knHandled;
        }
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstDying) {
      if (pmsg.mid === kmidReservedEnter) {
        // (the credits-share loss is #if 0'd out: sold processors keep credits)

        // Remove the docked miner with the processor
        const pgobMiner = this.ctx.gobm.GetGob(this.m_gidMiner, false) as MinerGob | null;
        if (pgobMiner !== null) {
          const tpt: TPoint = { tx: 0, ty: 0 };
          pgobMiner.GetTilePosition(tpt);
          trmap.ClearFlags(tpt.tx, tpt.ty, 1, 1, kbfStructure);

          pgobMiner.Deactivate();
          this.ctx.gobm.RemoveGob(pgobMiner);
          this.m_gidMiner = kgidNull;
        }

        // The door spot is reserved while being built; clearing is always safe
        this.clearDoorSpot(trmap);

        const nHandled = super.ProcessStateMachineMessage(st, pmsg);

        // Now that this gob is deactivated, redirect miners attempting delivery
        this.NotifyMinersAttemptingDelivery(true);
        return nHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }

  private clearDoorSpot(trmap: NonNullable<SimContext['trmap']>): void {
    trmap.ClearFlags(
      TcFromWc(this.m_wx) + 1,
      TcFromWc(this.m_wy) + this.m_pstruc.ctyTiles,
      1,
      1,
      kbfStructure,
    );
  }
}

// game/Processor.cpp:590-633 — the processing smoke: the processor's own 'smoke' strip
export class PuffGob extends AnimGob {
  // game/Processor.cpp:623-628 — AnimGob is not savable; Puff overrides back to savable
  override IsSavable(): boolean {
    return true;
  }

  // game/Processor.cpp:602-621 (knVerPuffGobState 1) — re-Init on the processor's
  // 'smoke' strip, then restore the anim + notify target
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    const panid = this.ctx.animDataFor(
      (this.ctx.unitConsts[kutProcessor] as StructConsts).szAniName,
    );
    this.m_ani.Init(panid);
    this.m_ani.SetStrip(panid.GetStripIndex('smoke'));
    this.m_ani.LoadState(pstm);
    this.m_smidNotify = pstm.ReadWord();
    this.m_wfAnm = kfAnmDeleteWhenDone | kfAnmSmokeFireLayer;
    return super.LoadState(pstm); // AnimGob has no serializer: Gob's runs
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    this.m_ani.SaveState(pstm);
    pstm.WriteWord(this.m_smidNotify);
    return super.SaveState(pstm);
  }

  InitPuff(wx: number, wy: number, smidNotify: number): boolean {
    const panid = this.ctx.animDataFor(
      (this.ctx.unitConsts[kutProcessor] as StructConsts).szAniName,
    );
    return this.Init(
      wx,
      wy,
      kfAnmDeleteWhenDone | kfAnmSmokeFireLayer,
      panid,
      panid.GetStripIndex('smoke'),
      smidNotify,
    );
  }

  override GetType(): number {
    return kgtPuff;
  }
}

// gobtypes: kgtPuff + the miner's gt for the delivery scan
import { kgtPuff, kgtGalaxMiner as kutGalaxMinerGt } from './gobtypes.ts';

// game/Processor.cpp:76-84 lives in structtypes.ts (CalcCreditsShare); re-import here
import { CalcCreditsShare as CalcCreditsShareLocal } from './structtypes.ts';

// self-register (see factory.ts)
registerGobType(kgtProcessor, (ctx) => new ProcessorGob(ctx));
registerGobType(kgtPuff, (ctx) => new PuffGob(ctx));
